// The cart side panel: the Shopper sees what the cart holds without leaving what they are
// browsing, takes a line out, sees the total cases and reaches the full cart.
//
// Opens over the current page (no navigation) as a modal dialog, portalled to <body>, in
// one of two Figma layouts chosen at the shared sheet boundary (`useSheetLayout`, 768px):
//   sheet  below 768px, a bottom sheet (5381:17345 in frame 5330:14365; with a cut-off,
//          5381:17372) whose top sits 56px below the viewport top: the grab handle, then
//          "Current order · N lines", the cut-off pill and Close, then a row with "Clear
//          cart" at the left, far from Close, then the lines, then a footer whose totals
//          breakdown is collapsed behind the "Total excl. VAT" toggle (expanded 5398:59600). A
//          downward drag on the header dismisses it like Close (`useSheetDrag`).
//   tray   from 768px, a 380px side tray at the right edge (tablet 5330:15682, with a
//          cut-off 5330:16161; desktop 4981:2221): the cart icon and title, the cut-off
//          pill and Close, then Clear cart and the lines pill.
// It is not the design-system Modal: that primitive draws a fixed title-and-Close header
// and a padded body, and this panel needs its own header rows and sticky footer. It shares
// the Modal's dialog contract through the same hooks: `useFocusTrap` moves focus in, traps
// Tab and restores focus to the header trigger on close, `useBodyScrollLock` locks the page,
// and Escape, Close and a scrim press dismiss.
//
// Extension points: Story 8.9's Clear (`ClearCartControl`) is mounted in the header's
// second row directly; `headerActions` is a separate slot rendered at the end of that row.
// Story 8.15 (ADO-2024), D-2 and DV-1: the Auto-send control (`AutoSendToggle`, "Auto-send 1 min
// before cut-off") is mounted in that row directly too, after the lines pill and before the slot,
// rather than through the slot: it takes the panel's own cut-off read (the status the title row's
// pill shows), and the row must know whether it shows to stay visible for it on an empty cart.
//
// Story 8.5 (ADO-2014), D-21 (superseding D-16's cut-off row): the header has two rows in
// one container that stays put above the scrolling lines. Row 1 holds the title, the session
// depot's `CutOffPill` (compact on phones) and Close; row 2 holds Clear cart and, in the tray,
// the lines pill, after which Story 8.15 adds its Auto-send checkbox and copy. The count is the
// cart's distinct lines, as the header cart badge and the depot switcher count them
// (`useCartLineCount`). The tray's title carries the cart icon; the sheet has no icon and puts
// the count as text after the title. The cut-off pill shows only for a cut-off (not for NONE,
// loading, an error or an exempt depot). The read is shared with the header chip
// (`useDepotCutOff`), and taken only while the panel is open.
// Before the session depot's basket has loaded the panel says it is loading, and when
// that read failed it says the lines could not be loaded, with the /cart page's "Reload
// your lines" retry (which re-runs the hydrate); it says "Your cart is empty." only once
// the basket has loaded empty. The line count and Total cases read as unknown until then
// (`useCartLineCount`, `useCartCaseCount`), never 0. Lines this session wrote before a
// failed read are still listed under the alert.
// Each row shows its line value over its locked unit price per case (`CartLineRow`). The
// footer carries the free-delivery progress (Story 8.8, below; its € spend and threshold are
// that read's own figures), "Total cases", "Total excl. VAT" (`useCartValue`: the lines summed
// at the prices the server locked on them, which are net of VAT; Story 8.11) and "Go to Order
// Review" (-> /cart). The order total reads as unknown under the same rule as the cases, and
// while a line has no locked price for any reason but its own add still being in flight
// (`settledCartValue`). ADO-2246: "Total deposit" (`useCartDeposit`: the lines' locked deposits
// summed in whole cents, over the lines "Total excl. VAT" counts, never part of it) sits between
// "Total cases" and "Total excl. VAT" (in the sheet, inside the collapsed breakdown with Total
// cases). It is hidden when the total is known and zero, and reads "–" when unknown
// (`showsDepositTotal`).
// Pricing migration 18 (ADO-2249, Task 9.2; Figma set 3, DEC-13 b): "Total saving €X" in green,
// with "Promotions €X" small and muted beneath it (the same figure: only a promotion line carries
// a saving, DEC-6), follows "Total deposit" (in the sheet, inside the breakdown with it), on every
// entry except WOW (`useIsWowEntry`). It is not drawn while no line carries `priceKind` or
// VITE_REAL_PRICES_ENABLED is off (`useCartSavings`, DEC-18), and otherwise by the deposit's rule:
// shown above zero, "–" when unknown, hidden at a known zero.
// Story 8.8 (ADO-2017), AC1: the footer is a sticky footer (Figma 5014:3963) that opens with
// the compact free-delivery progress (5009:3121) for the session depot, above the totals. It
// is read only while the panel is open (`useFreeDeliveryStatus`), and draws nothing while it
// loads, when it fails, or for a depot with no threshold.
// Story 8.12 (ADO-2021): while the panel is open it asks which of its lines the Customer
// already has on order (`useOnOrder`, the cache the review and browse surfaces share), and each
// line on order carries the on-order chip.
// The sheet slides up as it opens (CSS) and back down before it closes: Close, Escape, a scrim
// press and a drag mark it `data-closing`, and `onClose` runs when the slide ends (or after its
// length, should the browser never report the end). ADO-2262: the tray slides in from the right
// edge and back out the same way, over its own exit length. The Order Review link (which
// navigates away) and reduced motion close at once.
import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import {
  Collapse,
  CutOffPill,
  EXIT_GRACE_MS,
  FreeDeliveryProgress,
  Icon,
  MOTION_MS,
  prefersReducedMotion,
  useBodyScrollLock,
  useFocusTrap,
  useSheetDrag,
  useSheetLayout,
} from '../design-system';
import { CartLineRow, type RowControl } from './CartLineRow';
import { AutoSendToggle, useAutoSendShown } from './AutoSendToggle';
import { ClearCartControl } from './ClearCartControl';
import { focusNeighbourRow } from './cartRowFocus';
import { RemovedCartLine, usePendingRowFocus, useRemovedLineAction } from './RemovedCartLine';
import { focusOrderSentBanner, OrderSentBanner } from './OrderSentBanner';
import { withRemovedLines } from './removedLines';
import { useSessionDepotId } from '../availability/session-depot';
import { isCutOffShown } from '../shared/cutOffDisplay';
import { useDepotCutOff } from './useDepotCutOff';
import { linesLabel, showsDepositTotal, UNKNOWN_TOTAL } from './cartTotals';
import { useCartCaseCount, useCartLineCount } from './useCartCaseCount';
import { useCartDeposit, useCartSavings, useCartValue } from './useCartValue';
import { useIsWowEntry } from './wowEntry';
import { formatMoney, type MoneyValue } from '../shared/money';
import { useBasketStore } from './useBasketStore';
import { sessionCartTarget, useSessionCartTarget } from './sessionCart';
import { useFreeDeliveryStatus } from './useFreeDeliveryStatus';
import { useOnOrder } from '../onOrder/useOnOrder';
import { useOnOrderStore } from '../onOrder/useOnOrderStore';
import { useCoreMarks } from './useCoreMarks';
import { useCartDeals } from '../multibuy/useCartDeals';
import { useDealSessionStore } from '../multibuy/useDealSessionStore';
import { CartDealFlag, CartDealFlagLink, CartDealStrip } from '../multibuy/CartDealParts';
import { CartPanelDeal, CartPanelDealsToFinish } from '../multibuy/CartPanelDealViews';
import { useToastSlotStore } from '../toast/useToastSlotStore';
import { dismissLossToast } from '../multibuy/MultibuyCartWatcher';
import styles from './CartSidePanel.module.css';

/** Pricing migration 32 (ADO-3985), T14: what the panel's body shows (Option A, D-7): its lines,
 *  one deal (15) or the deals to finish (R8 16b), swapped in place, never a dialog on top. */
type PanelView =
  | { readonly kind: 'lines' }
  | {
      readonly kind: 'deal';
      readonly skuCode: string;
      readonly offerCode: string;
      readonly from: 'lines' | 'list';
    }
  | { readonly kind: 'list' };

/** The sheet's slide-down, in step with `.panel[data-layout='sheet'][data-closing]`. */
const SHEET_EXIT_MS = 200;
/** The tray's slide-out (`--motion-exit-slow`), in step with `.panel[data-layout='tray'][data-closing]`. */
const TRAY_EXIT_MS = MOTION_MS.exitSlow;

export interface CartSidePanelProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Extra header slot, rendered at the end of the second header row. */
  readonly headerActions?: ReactNode;
  /** DOM id of the dialog, for the trigger's `aria-controls`. */
  readonly id?: string;
}

export function CartSidePanel({ open, onClose, headerActions, id }: CartSidePanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const headingId = useId();
  const breakdownId = useId();
  const lines = useBasketStore((s) => s.lines);
  // Story 8.3 (ADO-2012), Task 11.5: a line removed while the Shopper was editing it stays in
  // the list, where it was, as a Removed Line (card 03).
  const lineConflicts = useBasketStore((s) => s.lineConflicts);
  // Story 8.3 (ADO-2012), card 10: under the Order sent banner the empty cart is the new one.
  const orderSent = useBasketStore((s) => s.orderSent !== null);
  const entries = withRemovedLines(lines, lineConflicts);
  const sheet = useSheetLayout();
  // The sheet's totals breakdown (Total cases) starts collapsed, as Figma draws it.
  const [totalsExpanded, setTotalsExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const animatesClose = !prefersReducedMotion();
  const exitMs = sheet ? SHEET_EXIT_MS : TRAY_EXIT_MS;
  const requestClose = useCallback(() => {
    if (animatesClose) setClosing(true);
    else onClose();
  }, [animatesClose, onClose]);
  // Read through a ref so a host passing an inline `onClose` does not restart the fallback
  // timer below on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  const finishClose = useCallback(() => {
    setClosing(false);
    onCloseRef.current();
  }, []);
  const sheetDrag = useSheetDrag(open && sheet && !closing, panelRef, requestClose);
  const trapTab = useFocusTrap(open, panelRef);
  // Story 8.3 (ADO-2012), card 10: declared after the trap, so on the opening the trap has already
  // moved focus in and noted where to return it (the control in use) before focus moves to the
  // Order sent banner; a notice arriving while the panel is open moves it the same way.
  const orderSentNotice = useBasketStore((s) => s.orderSent);
  useEffect(() => {
    // ADO-3257 (story D-6): a read's notice (no product) took no row from the Shopper: no move.
    if (!open || orderSentNotice === null || orderSentNotice.skuCode === null) return;
    focusOrderSentBanner(panelRef.current);
  }, [open, orderSentNotice]);
  const cases = useCartCaseCount();
  const lineCount = useCartLineCount();
  const linesText = lineCount === null ? UNKNOWN_TOTAL : linesLabel(lineCount);
  const orderTotal = useCartValue();
  const depositTotal = useCartDeposit();
  const savings = useCartSavings();
  const wowEntry = useIsWowEntry();
  const sessionDepotId = useSessionDepotId();
  const loadFailed = useBasketStore((s) => s.error);
  const hydrated = useBasketStore((s) => s.hydrated);
  // Waits only for a cart that will be read: a staff user with no store account picked has a
  // depot but no cart (Enabler E8-G, DA-10), so their panel is empty, never loading.
  const sessionCart = useSessionCartTarget();
  const loading = sessionCart !== null && !hydrated && !loadFailed;
  const cutOff = useDepotCutOff(open ? sessionCart : null);
  const autoSendShown = useAutoSendShown(cutOff.status);
  const actionsRowHidden =
    sheet && lines.length === 0 && headerActions === undefined && !autoSendShown;
  const retryLoad = useCallback(() => {
    const cart = sessionCartTarget();
    if (cart !== null) void useBasketStore.getState().hydrate(cart);
  }, [sessionDepotId]);
  const freeDelivery = useFreeDeliveryStatus(open ? sessionDepotId : null);
  // Read only while open: every full-header screen mounts the panel closed (`useCartPanel`).
  useOnOrder(open ? lines.map((line) => line.skuCode) : []);
  const onOrderBySku = useOnOrderStore((s) => s.bySku);
  // The lines' RBP core marks, read while open like the on-order chips.
  const coreMarks = useCoreMarks(open ? lines.map((line) => line.skuCode) : []);
  // Pricing migration 32 (ADO-3985), T14 and T16: the lines' deals, read while open, as above.
  const cartDeals = useCartDeals(open ? lines : []);
  const [view, setShownView] = useState<PanelView>({ kind: 'lines' });
  // Opening a deal in the panel closes a loss toast still up (R3 21w-b).
  const setView = useCallback((next: PanelView) => {
    if (next.kind === 'deal') dismissLossToast();
    setShownView(next);
  }, []);
  const dealsView = view.kind === 'lines' ? null : view;
  // On a swap, focus goes to the back link; back on the lines, to the heading (never <body>).
  useEffect(() => {
    if (!open) return;
    if (dealsView === null) return;
    panelRef.current?.querySelector<HTMLElement>('[data-panel-back]')?.focus();
  }, [open, dealsView?.kind, dealsView]);
  const lendToastSlot = useCallback((slot: HTMLDivElement | null) => {
    useToastSlotStore.getState().setSlot(slot);
  }, []);
  const showLines = useCallback(() => {
    setView({ kind: 'lines' });
    requestAnimationFrame(() => headingRef.current?.focus());
  }, []);
  // Every deal on the list finished: nothing is left to pick, so back to the lines, focus with
  // it (the list the focus was in is gone).
  const listEmptied = view.kind === 'list' && cartDeals.toFinish.length === 0;
  useEffect(() => {
    if (listEmptied) showLines();
  }, [listEmptied, showLines]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        requestClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, requestClose]);

  // The fallback for a slide whose `transitionend` never arrives; the timer is cleared once
  // the close lands, so a slide that does report its end closes only once.
  useEffect(() => {
    if (!closing) return;
    const timer = setTimeout(finishClose, exitMs + EXIT_GRACE_MS);
    return () => clearTimeout(timer);
  }, [closing, exitMs, finishClose]);

  // Counted with the Clear confirm Modal's own lock, so unmounting the panel while
  // the confirm is open does not leave the page locked (Story 8.9, ADO-2018).
  useBodyScrollLock(open);

  // AC4: a removed row hands focus to the same control on the next row, else the
  // previous row, else the panel heading, so it never drops to <body>.
  const focusAfterRemove = useCallback(
    (skuCode: string, control: RowControl) =>
      focusNeighbourRow(listRef.current, skuCode, control, () => headingRef.current?.focus()),
    [],
  );
  // Story 8.9 (ADO-2018): a confirmed clear removes every row, so it hands focus to
  // the heading.
  const focusAfterClear = useCallback(() => headingRef.current?.focus(), []);
  const removedLineAction = useRemovedLineAction(listRef);
  // Story 8.3 (ADO-2012), card 10: "Add to a new cart" hands focus to the line once it is back.
  const requestRowFocus = usePendingRowFocus(listRef);

  // Crossing the 768px sheet boundary while open switches the rows to `panelMobile`. The
  // row keeps its controls in the same parents, so focus normally survives; if a
  // focused control is ever unmounted by the switch, the browser drops focus to
  // <body>, out of reach of the Tab trap, so hand it to the heading as a removal does. Runs
  // only on a change of `sheet`, never on open: the focus trap records the element
  // to restore on close when the panel opens.
  const previousSheetRef = useRef(sheet);
  useLayoutEffect(() => {
    if (previousSheetRef.current === sheet) return;
    previousSheetRef.current = sheet;
    const panel = panelRef.current;
    if (panel === null || panel.contains(document.activeElement)) return;
    // Focus in another open modal (the Clear confirm, portalled to <body>) belongs there.
    const otherModal = document.activeElement?.closest('[aria-modal="true"]') ?? null;
    if (otherModal !== null && otherModal !== panel) return;
    headingRef.current?.focus();
  }, [sheet]);

  // A closed panel forgets the expanded breakdown, so it reopens as Figma draws it, and a
  // close the host made while the sheet was sliding down is not carried into the next open.
  useEffect(() => {
    if (open) return;
    setTotalsExpanded(false);
    setClosing(false);
    setView({ kind: 'lines' });
  }, [open]);

  if (!open) return null;

  const title = (
    <h2 id={headingId} ref={headingRef} tabIndex={-1} className={styles.title}>
      Current order
    </h2>
  );
  const closeButton = (
    <button
      type="button"
      className={styles.close}
      aria-label="Close current order"
      onClick={requestClose}
    >
      <Icon name="x" size={20} />
    </button>
  );
  const totalCasesRow = (
    <div className={styles.totalRow}>
      <span>Total cases</span>
      {/* The tray's live region for the cases (AC5): a step or a removal announces the
          new total, not only the touched row. The header's lines pill stays silent. In the
          sheet this row is hidden while the breakdown is collapsed, so it does not announce;
          the header count there announces the lines. */}
      <span
        className={styles.totalValue}
        data-testid="cart-total-cases"
        aria-live={sheet ? undefined : 'polite'}
      >
        {cases ?? UNKNOWN_TOTAL}
      </span>
    </div>
  );
  // ADO-2246: not a live region, the cases row owns the announcement of a changed total.
  const totalDepositRow = showsDepositTotal(depositTotal) ? (
    <div className={styles.totalRow}>
      <span>Total deposit</span>
      <span className={styles.totalValue} data-testid="cart-total-deposit">
        {depositTotal === null ? UNKNOWN_TOTAL : formatMoney(depositTotal)}
      </span>
    </div>
  ) : null;
  const savingsShown =
    !wowEntry && savings.sent && showsDepositTotal(savings.total) ? savings.total : undefined;
  const savingText = (total: MoneyValue | null) =>
    total === null ? UNKNOWN_TOTAL : formatMoney(total);
  const totalSavingRows =
    savingsShown === undefined ? null : (
      <div className={styles.savingBlock}>
        <div className={styles.totalRow}>
          <span>Total saving</span>
          <span
            className={`${styles.totalValue} ${styles.savingValue}`}
            data-testid="cart-total-saving"
          >
            {savingText(savingsShown)}
          </span>
        </div>
        <p className={styles.savingBreakdown} data-testid="cart-promotion-saving">
          Promotions {savingText(savingsShown)}
        </p>
      </div>
    );
  // ADO-3985 (R2 13w): on WOW the free cases show as a count, with no € value; row 18 shows no
  // savings on WOW (DEC-13), so the count stands alone.
  const freeCasesRow =
    wowEntry && cartDeals.freeCases.cases > 0 ? (
      <p className={styles.savingBreakdown} data-testid="cart-free-cases">
        Free cases {cartDeals.freeCases.cases}
      </p>
    ) : null;
  const flag = cartDeals.flag;
  const dealFlag =
    flag === null ? null : (
      <CartDealFlag
        className={styles.dealFlag}
        title={flag.title}
        body={flag.body}
        action={
          flag.deals.length === 1 ? (
            <CartDealFlagLink
              label="See qualifying lines"
              icon="arrowRight"
              onClick={() => {
                const deal = flag.deals[0];
                const sku = deal?.products.find((product) =>
                  lines.some((line) => line.skuCode === product.skuCode),
                )?.skuCode;
                if (deal !== undefined && sku !== undefined)
                  setView({
                    kind: 'deal',
                    skuCode: sku,
                    offerCode: deal.multibuyOfferCode,
                    from: 'lines',
                  });
              }}
            />
          ) : (
            <CartDealFlagLink
              label="Show me"
              icon="arrowDown"
              onClick={() => setView({ kind: 'list' })}
            />
          )
        }
        onDismiss={() =>
          useDealSessionStore.getState().dismissFlags(
            flag.deals.map((deal) => ({
              offerCode: deal.multibuyOfferCode,
              pooled: deal.progress.pooledQuantity,
            })),
          )
        }
      />
    );
  // ADO-3985 (A4, 14, R2 12w, R1 12s, R3 21w): a line's strip opens its deal in the panel (15).
  const dealStripOf = (sku: string) => {
    const strip = cartDeals.lineStrip(sku);
    return strip === null ? undefined : (
      <CartDealStrip
        strip={strip}
        onOpen={() => {
          if (strip.deal !== null)
            setView({
              kind: 'deal',
              skuCode: sku,
              offerCode: strip.deal.multibuyOfferCode,
              from: 'lines',
            });
        }}
      />
    );
  };
  const orderTotalValue = (
    <span className={styles.orderTotalValue} data-testid="cart-order-total">
      {orderTotal === null ? UNKNOWN_TOTAL : formatMoney(orderTotal)}
    </span>
  );

  // Portalled to <body> so the scrim covers the whole page regardless of the
  // sticky header's stacking context it is declared in.
  return createPortal(
    // Backdrop click is a pointer-only convenience; Escape and the close button
    // are the keyboard paths (WAI-ARIA dialog pattern).
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={styles.overlay}
      data-layout={sheet ? 'sheet' : 'tray'}
      data-closing={closing ? '' : undefined}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      {/* The keydown handler implements the dialog's Tab trap. */}
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
      <div
        ref={panelRef}
        id={id}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        className={styles.panel}
        data-layout={sheet ? 'sheet' : 'tray'}
        data-closing={closing ? '' : undefined}
        onKeyDown={trapTab}
        onTransitionEnd={(e) => {
          if (closing && e.target === e.currentTarget && e.propertyName === 'transform') {
            finishClose();
          }
        }}
      >
        {/* In the sheet the whole header, both rows, is the drag handle. ADO-3985: with a deal or
            the deals to finish open, the back link stands in for it (15, 16b); the title still
            names the dialog. */}
        <div
          className={styles.stickyHeader}
          hidden={dealsView !== null}
          {...(sheet ? sheetDrag : {})}
        >
          {sheet ? (
            <span className={styles.grabHandle} data-part="grab-handle" aria-hidden="true" />
          ) : null}
          {/* Row 1, one tree for both layouts, so a control keeps its state across the
              768px switch; CSS places each part per `data-layout`. The sheet draws its grab
              handle above this row; the tray its cart icon before the title. The line count
              follows the title as text in the sheet, and is a pill in row 2 in the tray. */}
          <div
            className={styles.header}
            data-testid="cart-panel-title-row"
            data-part={sheet ? 'sheet-header' : 'tray-header'}
            data-last-row={actionsRowHidden ? 'true' : undefined}
          >
            <div className={styles.titleGroup}>
              {sheet ? null : <Icon name="shoppingCart" size={22} />}
              {title}
              {sheet ? (
                <span className={styles.linesText} aria-live="polite">
                  <span aria-hidden="true">·</span>
                  <span>{linesText}</span>
                </span>
              ) : null}
            </div>
            {isCutOffShown(cutOff.status, cutOff.countdown) ? (
              <CutOffPill {...cutOff} phoneSize="compact" />
            ) : null}
            {closeButton}
          </div>
          {/* Row 2: Clear cart, then the lines pill in the tray, then the host's actions. The
              sheet draws the count in row 1, so an empty cart (no Clear cart) and no host
              actions leave this row nothing to show: it is hidden, not unmounted, so
              `ClearCartControl` survives a confirmed clear and moves focus to the heading. */}
          <div
            className={styles.actionsRow}
            data-testid="cart-panel-actions-row"
            data-part="header-actions"
            hidden={actionsRowHidden}
          >
            {/* Story 8.9 (ADO-2018): Clear cart, a text link in both layouts. */}
            <ClearCartControl appearance="link" onCleared={focusAfterClear} />
            {sheet ? null : (
              <span className={styles.linesChip} data-part="lines-pill">
                {linesText}
              </span>
            )}
            {/* Story 8.15 (ADO-2024): hidden where the cart has no cut-off (D-2). */}
            <AutoSendToggle variant="panel" cutOffStatus={cutOff.status} onNavigate={onClose} />
            {headerActions}
          </div>
        </div>
        {dealsView?.kind === 'deal' ? (
          <CartPanelDeal
            skuCode={dealsView.skuCode}
            offerCode={dealsView.offerCode}
            backLabel={
              dealsView.from === 'list'
                ? cartDeals.toFinish.length === 1
                  ? '1 deal'
                  : `${cartDeals.toFinish.length} deals`
                : 'Current order'
            }
            onBack={dealsView.from === 'list' ? () => setView({ kind: 'list' }) : showLines}
            onDone={showLines}
            touch={sheet}
          />
        ) : dealsView?.kind === 'list' ? (
          <CartPanelDealsToFinish
            rows={cartDeals.toFinish}
            onBack={showLines}
            onOpen={(deal) => {
              const sku = deal.products.find((product) =>
                lines.some((line) => line.skuCode === product.skuCode),
              )?.skuCode;
              if (sku !== undefined)
                setView({
                  kind: 'deal',
                  skuCode: sku,
                  offerCode: deal.multibuyOfferCode,
                  from: 'list',
                });
            }}
          />
        ) : null}
        <div className={styles.body} hidden={dealsView !== null}>
          {/* ADO-3985 (14): the incomplete multi-buy flag, pinned at the top of the lines. */}
          {dealFlag}
          {/* Story 8.3 (ADO-2012), card 10: a change refused because the order had gone. */}
          <OrderSentBanner
            layout="stacked"
            manageFocus={false}
            onAddToNewCart={(skuCode, added) =>
              requestRowFocus(skuCode, 'quantity', added, { untilLanded: true })
            }
          />
          {loadFailed ? (
            <div className={styles.loadError} role="alert">
              <p>We couldn&apos;t load your cart lines.</p>
              <button type="button" className={styles.retryButton} onClick={retryLoad}>
                Reload your lines
              </button>
            </div>
          ) : null}
          {entries.length === 0 ? (
            loadFailed ? null : loading ? (
              <p className={styles.empty} aria-live="polite">
                Loading your cart…
              </p>
            ) : (
              <p className={styles.empty}>
                {orderSent ? 'Your new cart is empty.' : 'Your cart is empty.'}
              </p>
            )
          ) : (
            <ul className={styles.list} ref={listRef}>
              {entries.map((entry) => {
                if (entry.kind === 'removed') {
                  return (
                    <RemovedCartLine
                      key={entry.skuCode}
                      skuCode={entry.skuCode}
                      name={entry.name}
                      message={entry.view.message}
                      action={entry.view.action}
                      announce={entry.announce}
                      variant={sheet ? 'panelMobile' : 'panel'}
                      onAction={removedLineAction}
                    />
                  );
                }
                const { line } = entry;
                const onOrder = onOrderBySku[line.skuCode];
                return (
                  <CartLineRow
                    key={line.skuCode}
                    line={line}
                    variant={sheet ? 'panelMobile' : 'panel'}
                    onFocusAfterRemove={focusAfterRemove}
                    onProductLinkClick={onClose}
                    conflict
                    {...(onOrder !== undefined && onOrder !== 'none' ? { onOrder } : {})}
                    {...(coreMarks[line.skuCode] !== undefined
                      ? { coreMark: coreMarks[line.skuCode] }
                      : {})}
                    deal={dealStripOf(line.skuCode)}
                  />
                );
              })}
            </ul>
          )}
        </div>
        {/* ADO-3985 (R3 21w, 21s): the toast's place while the panel is open, above the totals. */}
        <div className={styles.toastSlot} ref={lendToastSlot} data-part="toast-slot" />
        <div
          className={styles.stickyFooter}
          data-testid="cart-panel-sticky-footer"
          hidden={dealsView !== null}
        >
          <FreeDeliveryProgress status={freeDelivery} variant="compact" />
          <div
            className={styles.footer}
            data-totals-expanded={sheet && totalsExpanded ? '' : undefined}
          >
            {sheet ? (
              <>
                <Collapse
                  id={breakdownId}
                  open={totalsExpanded}
                  className={styles.breakdownCollapse}
                >
                  <div className={styles.breakdown}>
                    {totalCasesRow}
                    {totalDepositRow}
                    {totalSavingRows}
                    {freeCasesRow}
                  </div>
                </Collapse>
                <div className={styles.totalRow}>
                  <button
                    type="button"
                    className={styles.totalsToggle}
                    aria-expanded={totalsExpanded}
                    aria-controls={breakdownId}
                    onClick={() => setTotalsExpanded((expanded) => !expanded)}
                  >
                    Total excl. VAT
                    <Icon name="caretDown" size={16} />
                  </button>
                  {orderTotalValue}
                </div>
              </>
            ) : (
              <>
                {totalCasesRow}
                {totalDepositRow}
                {totalSavingRows}
                {freeCasesRow}
                <div className={styles.totalRow}>
                  <span>Total excl. VAT</span>
                  {orderTotalValue}
                </div>
              </>
            )}
            <Link to="/cart" className={styles.reviewLink} onClick={onClose}>
              Go to Order Review
            </Link>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
